import React from 'react';
import { List, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface EventSummaryCardProps {
  title: string;
  description?: string;
  isSystemTemplate?: boolean;
  activityCount?: number;
  goalCount?: number;
  metaPrefix?: React.ReactNode;
  fallbackMetaText?: string;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  extraBadges?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  statsClassName?: string;
  activityHighlighted?: boolean;
  goalHighlighted?: boolean;
  onActivityClick?: () => void;
  onGoalClick?: () => void;
}

export const EventSummaryCard: React.FC<EventSummaryCardProps> = ({
  title,
  description,
  isSystemTemplate,
  activityCount,
  goalCount,
  metaPrefix,
  fallbackMetaText,
  leading,
  trailing,
  extraBadges,
  className,
  titleClassName,
  descriptionClassName,
  statsClassName,
  activityHighlighted,
  goalHighlighted,
  onActivityClick,
  onGoalClick,
}) => {
  const renderActivityMeta = () => {
    if (typeof activityCount !== 'number') {
      return null;
    }

    const content = (
      <>
        <List className="h-3 w-3" />
        {activityCount} activities
      </>
    );

    const classNames = cn(
      'flex items-center gap-1 transition-colors',
      onActivityClick ? 'hover:text-indigo-600' : '',
      activityHighlighted ? 'text-indigo-600 font-medium' : '',
    );

    if (!onActivityClick) {
      return <span className={classNames}>{content}</span>;
    }

    return (
      <button
        type="button"
        className={classNames}
        onClick={onActivityClick}
        aria-label={`Show activities for ${title}`}
      >
        {content}
      </button>
    );
  };

  const renderGoalMeta = () => {
    if (typeof goalCount !== 'number') {
      return null;
    }

    const content = (
      <>
        <Target className="h-3 w-3" />
        {goalCount} goals
      </>
    );

    const classNames = cn(
      'flex items-center gap-1 transition-colors',
      onGoalClick ? 'hover:text-green-600' : '',
      goalHighlighted ? 'text-green-600 font-medium' : '',
    );

    if (!onGoalClick) {
      return <span className={classNames}>{content}</span>;
    }

    return (
      <button
        type="button"
        className={classNames}
        onClick={onGoalClick}
        aria-label={`Show goals for ${title}`}
      >
        {content}
      </button>
    );
  };

  const showFallbackMeta = typeof activityCount !== 'number' && typeof goalCount !== 'number' && !!fallbackMetaText;

  return (
    <div className={cn('rounded-lg border bg-background p-3 transition-colors', className)}>
      <div className="flex items-start gap-3">
        {leading}

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <h4 className={cn('text-sm font-medium', titleClassName)}>{title}</h4>
            {isSystemTemplate && (
              <Badge variant="secondary" className="text-[10px]">
                System
              </Badge>
            )}
            {extraBadges}
          </div>

          {description && (
            <p className={cn('text-sm text-muted-foreground', descriptionClassName)}>{description}</p>
          )}

          <div className={cn('mt-2 flex items-center gap-4 text-xs text-muted-foreground', statsClassName)}>
            {metaPrefix}
            {renderActivityMeta()}
            {renderGoalMeta()}
            {showFallbackMeta && (
              <span className="uppercase tracking-wide">{fallbackMetaText}</span>
            )}
          </div>
        </div>

        {trailing}
      </div>
    </div>
  );
};
